import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ChangeDetectionStrategy, inject, forwardRef, input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SbSelectItem } from '../sb-select/sb-select-base.component';
import type { ConditionVariable } from './sb-text-condition.service';
import {
  buildText,
  ConditionOperator,
  ConditionSpec,
  quote,
  ruleProblem,
  scanText,
  TextBlockContent,
  TextRule,
} from './text-condition-grammar';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SbButtonComponent } from '../sb-button/sb-button.component';
import { SbInputSelectComponent } from '../sb-select/sb-select.component';
import { SbInputNumberComponent } from '../sb-input/sb-input-number.component';
import { SbInputTextComponent } from '../sb-input/sb-input-text.component';
import { SbTextareaComponent } from '../sb-textarea/sb-textarea.component';
import { LowerCasePipe } from '@angular/common';

export type SbTextConditionDialogResult = { content: TextBlockContent } | { removed: true };

interface RuleRow {
  uid: number;
  variable: string;
  operator: string;
  value: string;
  /** Problems show once the user has been at the row (or it came from the field). */
  touched: boolean;
  variableItems: SbSelectItem[];
  operatorItems: SbSelectItem[];
  optionItems: SbSelectItem[];
}

interface CaseModel {
  uid: number;
  match: string;
  rules: RuleRow[];
  text: string;
}

let nextUid = 0;

/**
 * The dialog behind a field's "Condition" button: rules over the variables, the text they show,
 * and what shows otherwise. Operators, their wording and every limit come from the spec, so it
 * says what the server's ConditionEditor says.
 */
@Component({
  selector: 'app-sb-text-condition-dialog',
  templateUrl: './sb-text-condition-dialog.component.html',
  styleUrls: ['./sb-text-condition-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormsModule, SbButtonComponent, forwardRef(() => SbInputSelectComponent), forwardRef(() => SbInputNumberComponent), forwardRef(() => SbInputTextComponent), forwardRef(() => SbTextareaComponent), LowerCasePipe]
})
export class SbTextConditionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly modal = inject(NgbActiveModal);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly spec = input<ConditionSpec>(undefined);
  readonly variables = input<ConditionVariable[]>([]);
  /** The block being edited, or null for a new one. */
  readonly content = input<TextBlockContent | null>(null);
  /** The words selected in the field, offered as the text to show. */
  readonly selectedText = input('');

  cases: CaseModel[] = [];
  otherwise = '';
  editing = false;
  matchItems: SbSelectItem[] = [];
  private initialised = false;
  private byKey = new Map<string, ConditionVariable>();

  ngOnInit(): void {
    this.init();
  }

  ngAfterViewInit(): void {
    // Start where the work is: the first rule's variable.
    setTimeout(() => {
      const target = this.host.nativeElement.querySelector<HTMLElement>(
        '.sb-text-condition__rule input:not([type=hidden])',
      );
      target?.focus();
    });
    // A select swallows Esc (it prevents the default), so the modal would never hear it. Read it
    // first: it closes an open list, and otherwise cancels the dialog.
    this.host.nativeElement.addEventListener('keydown', this.onEscape, true);
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('keydown', this.onEscape, true);
  }

  private readonly onEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.host.nativeElement.querySelector('.ng-select-opened')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.cancel();
  };

  /** Builds the form from the inputs; safe to call more than once. */
  init(): void {
    const spec = this.spec();
    if (this.initialised || !spec) {
      return;
    }
    this.initialised = true;
    this.byKey = new Map(this.variables().map((variable) => [variable.key.toUpperCase(), variable]));
    this.matchItems = (spec.matchModes || []).map((mode) => ({ id: mode.id, name: mode.label }));
    this.editing = !!this.content();
    const content = this.content();
    if (content) {
      this.cases = content.cases.map((item) => this.caseOf(item.condition.match, item.condition.rules, item.text, true));
      this.otherwise = content.otherwise || '';
    } else {
      this.cases = [this.caseOf(this.defaultMatch, [], this.selectedText() || '', false)];
      this.addRule(this.cases[0]);
    }
  }

  get title(): string {
    const spec = this.spec();
    return (spec.help && spec.help.title) || 'Display conditions';
  }

  /** "Show this text" / "Otherwise show", from the spec's text help. */
  get thenLabel(): string {
    const spec = this.spec();
    return (spec.text && spec.text.help && spec.text.help.then) || 'Show this text';
  }

  get otherwiseLabel(): string {
    const spec = this.spec();
    return (spec.text && spec.text.help && spec.text.help.otherwise) || 'Otherwise show';
  }

  /** "Show", from the spec's first action. */
  get showWord(): string {
    const action = (this.spec().actions || [])[0];
    return action ? action.label : 'Show';
  }

  get maxRules(): number {
    return Number(this.spec().maxRules) || Infinity;
  }

  get defaultMatch(): string {
    return this.matchItems.length ? String(this.matchItems[0].id) : '';
  }

  trackByUid(_index: number, item: { uid: number }): number {
    return item.uid;
  }

  variableOf(row: RuleRow): ConditionVariable | null {
    return row.variable ? this.byKey.get(row.variable.toUpperCase()) || null : null;
  }

  operatorOf(row: RuleRow): ConditionOperator | null {
    return (this.spec().operators || []).find((operator) => operator.id === row.operator) || null;
  }

  /** `description · Sample: …` under a rule. */
  hintOf(row: RuleRow): string {
    const variable = this.variableOf(row);
    if (!variable) {
      return '';
    }
    const parts = [];
    if (variable.description) {
      parts.push(variable.description);
    }
    if (variable.sample !== '') {
      const sample = variable.sample.trim();
      parts.push(`Sample: ${sample.length > 80 ? `${sample.slice(0, 79)}…` : sample}`);
    }
    return parts.join(' · ');
  }

  helpOf(row: RuleRow): string {
    const operator = this.operatorOf(row);
    return operator && operator.help ? operator.help : '';
  }

  needsValue(row: RuleRow): boolean {
    const operator = this.operatorOf(row);
    return !!(operator && operator.needsValue);
  }

  isNumeric(row: RuleRow): boolean {
    const operator = this.operatorOf(row);
    return !!(operator && operator.numeric);
  }

  hasOptions(row: RuleRow): boolean {
    return !this.isNumeric(row) && row.optionItems.length > 0;
  }

  problemOf(row: RuleRow): string | null {
    return ruleProblem(this.ruleOf(row), this.spec());
  }

  /** Advice that does not stop saving: a variable or value the panel does not know. */
  warningOf(row: RuleRow): string | null {
    if (row.variable && this.variables().length && !this.variableOf(row)) {
      return `#${row.variable}# is not a variable this field knows, so this text is never shown.`;
    }
    const variable = this.variableOf(row);
    const operator = this.operatorOf(row);
    if (variable && operator && !this.suits(operator, variable)) {
      return `${quote(operator.label)} does not suit ${variable.name}.`;
    }
    if (this.hasOptions(row) && row.value && !variable.options.some((option) => option.value === row.value)) {
      return `${quote(row.value)} is not one of the values ${variable.name} can have.`;
    }
    return null;
  }

  /** The problem shown under a row: only once it has been touched. */
  shownProblem(row: RuleRow): string | null {
    return row.touched ? this.problemOf(row) : null;
  }

  onVariableChange(row: RuleRow, key: unknown): void {
    row.variable = key == null ? '' : String(key);
    row.touched = true;
    const variable = this.variableOf(row);
    row.operatorItems = this.operatorItemsOf(row);
    const operator = this.operatorOf(row);
    if (!operator || (variable && !this.suits(operator, variable))) {
      row.operator = this.defaultOperatorOf(variable);
    }
    row.optionItems = this.optionItemsOf(row);
    if (row.optionItems.length && !row.optionItems.some((item) => item.id === row.value)) {
      row.value = '';
    }
  }

  onOperatorChange(row: RuleRow, id: unknown): void {
    row.operator = id == null ? '' : String(id);
    row.touched = true;
    row.optionItems = this.optionItemsOf(row);
  }

  onValueChange(row: RuleRow, value: unknown): void {
    row.value = value == null ? '' : String(value);
    row.touched = true;
  }

  addRule(item: CaseModel): void {
    if (item.rules.length >= this.maxRules) {
      return;
    }
    const row = this.rowOf({ variable: '', operator: '' }, false);
    item.rules.push(row);
    this.focusLater(`[data-rule="${row.uid}"] input`);
  }

  removeRule(item: CaseModel, row: RuleRow): void {
    item.rules = item.rules.filter((candidate) => candidate !== row);
    if (!item.rules.length) {
      this.addRule(item);
    }
  }

  addCase(): void {
    const item = this.caseOf(this.defaultMatch, [], '', false);
    this.cases.push(item);
    this.addRule(item);
  }

  removeCase(item: CaseModel): void {
    this.cases = this.cases.filter((candidate) => candidate !== item);
  }

  /** Everything that stops saving, in order. */
  get problems(): string[] {
    const problems: string[] = [];
    const variables = this.variables();
    const known = variables.length ? variables.map((variable) => variable.key) : null;
    const textProblem = (label: string, text: string) => {
      const found = scanText(text, this.spec(), { knownVariables: known }).problems[0];
      if (found) {
        problems.push(`${label}: ${quote(found.tag)} ${found.message}`);
      }
    };
    this.cases.forEach((item, caseIndex) => {
      const prefix = this.cases.length > 1 ? `Case ${caseIndex + 1}, rule` : 'Rule';
      item.rules.forEach((row, index) => {
        const problem = this.problemOf(row);
        if (problem) {
          problems.push(`${prefix} ${index + 1}: ${problem}`);
        }
      });
      textProblem(this.cases.length > 1 ? `Case ${caseIndex + 1} text` : 'Text', item.text);
    });
    textProblem('Otherwise', this.otherwise);
    if (!this.cases.some((item) => item.text.trim()) && !this.otherwise.trim()) {
      problems.push('Write the text to show.');
    }
    return problems;
  }

  get canSave(): boolean {
    return this.problems.length === 0;
  }

  /** What Save writes into the field, for the preview line. */
  get preview(): string {
    return this.canSave ? buildText(this.currentContent(), this.spec()) : '';
  }

  save(): void {
    if (!this.canSave) {
      this.cases.forEach((item) => item.rules.forEach((row) => (row.touched = true)));
      return;
    }
    this.modal.close({ content: this.currentContent() } as SbTextConditionDialogResult);
  }

  remove(): void {
    this.modal.close({ removed: true } as SbTextConditionDialogResult);
  }

  cancel(): void {
    this.modal.dismiss('cancel');
  }

  /** Enter in a rule's value saves (a select or button keeps its own Enter). */
  onValueKeydown(event: KeyboardEvent, row: RuleRow): void {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    row.touched = true;
    this.save();
  }

  private currentContent(): TextBlockContent {
    return {
      cases: this.cases.map((item) => ({
        condition: { match: item.match || this.defaultMatch, rules: item.rules.map((row) => this.ruleOf(row)) },
        text: item.text,
      })),
      otherwise: this.otherwise ? this.otherwise : null,
    };
  }

  private ruleOf(row: RuleRow): TextRule {
    const rule: TextRule = { variable: row.variable, operator: row.operator };
    if (this.needsValue(row)) {
      rule.value = String(row.value == null ? '' : row.value).trim();
    }
    return rule;
  }

  private caseOf(match: string, rules: TextRule[], text: string, touched: boolean): CaseModel {
    return {
      uid: ++nextUid,
      match: match || this.defaultMatch,
      rules: rules.map((rule) => this.rowOf(rule, touched)),
      text,
    };
  }

  private rowOf(rule: TextRule, touched: boolean): RuleRow {
    const row: RuleRow = {
      uid: ++nextUid,
      variable: rule.variable || '',
      operator: rule.operator || '',
      value: rule.value == null ? '' : String(rule.value),
      touched,
      variableItems: [],
      operatorItems: [],
      optionItems: [],
    };
    row.variableItems = this.variableItemsOf(row);
    row.operatorItems = this.operatorItemsOf(row);
    row.optionItems = this.optionItemsOf(row);
    return row;
  }

  /** An operator suits a variable when their value types meet (either side unset = all). */
  private suits(operator: ConditionOperator, variable: ConditionVariable | null): boolean {
    const wanted = operator.valueTypes || [];
    const types = variable ? variable.valueType : [];
    return !wanted.length || !types.length || types.some((type) => wanted.includes(type));
  }

  /** Variables with allowed values start on the first comparison that takes one of them. */
  private defaultOperatorOf(variable: ConditionVariable | null): string {
    const offered = (this.spec().operators || []).filter((operator) => this.suits(operator, variable));
    if (variable && variable.options.length) {
      const choice = offered.find((operator) => operator.needsValue && !operator.numeric);
      if (choice) {
        return choice.id;
      }
    }
    return (offered[0] || (this.spec().operators || [])[0] || { id: '' }).id;
  }

  private variableItemsOf(row: RuleRow): SbSelectItem[] {
    const items: SbSelectItem[] = this.variables().map((variable) => ({ id: variable.key, name: variable.name }));
    if (row.variable && !this.byKey.has(row.variable.toUpperCase())) {
      items.unshift({ id: row.variable, name: `#${row.variable}# (not available)` });
    }
    return items;
  }

  private operatorItemsOf(row: RuleRow): SbSelectItem[] {
    const variable = this.variableOf(row);
    return (this.spec().operators || [])
      .filter((operator) => this.suits(operator, variable) || operator.id === row.operator)
      .map((operator) => ({ id: operator.id, name: operator.label }));
  }

  private optionItemsOf(row: RuleRow): SbSelectItem[] {
    const variable = this.variableOf(row);
    if (!variable || !variable.options.length || this.isNumeric(row)) {
      return [];
    }
    const items: SbSelectItem[] = variable.options.map((option) => ({ id: option.value, name: option.label }));
    if (row.value && !items.some((item) => item.id === row.value)) {
      items.push({ id: row.value, name: `${quote(row.value)} (not listed)` });
    }
    return items;
  }

  private focusLater(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }
}
